// PARA filing shared by web and mobile: where an item lives, what the AI
// suggested, GTD's status and context for tasks, and the request the
// file_capture_into() database function takes.

// The .ts extension lets `node --test` run the source directly.
import { itemTextFromCapture } from './captureText.ts';
import { isWallTime, wallTimeIn, wallTimePhrase } from './reminders.ts';
import { captureTitle, isCalendarDate } from './today.ts';
import type { ContainerRef } from './gtd.ts';
import type { ContainerKind, ContainerRow, GtdStatus, ItemKind } from './types';

// Where an item lives, which items Home hides, and which projects are stuck
// live in gtd.ts, which generate-digest imports too.
export { containerOf, stuckProjects, withoutArchived } from './gtd.ts';
export type { ContainerRef } from './gtd.ts';

export const KIND_LABELS: Record<ItemKind, string> = { task: 'Task', note: 'Note' };

export const CONTAINER_KIND_LABELS: Record<ContainerKind, string> = {
  project: 'Project',
  area: 'Area',
  resource: 'Resource',
};

export const CONTAINER_KIND_PLURALS: Record<ContainerKind, string> = {
  project: 'Projects',
  area: 'Areas',
  resource: 'Resources',
};

export const GTD_STATUSES: readonly GtdStatus[] = ['next', 'waiting', 'someday'];

export const GTD_STATUS_LABELS: Record<GtdStatus, string> = {
  next: 'Next action',
  waiting: 'Waiting for',
  someday: 'Someday',
};

/** The longest GTD context a task can store, and the most a profile keeps. */
export const CONTEXT_MAX_LENGTH = 40;
export const CONTEXTS_MAX = 30;

/**
 * How a GTD context is compared: newlines and '|' become spaces, runs of
 * spaces one, no surrounding spaces, at most 80 characters, ignoring case.
 * The database (private.context_key) and the classifier use the same rule.
 */
export function contextKey(value: string): string {
  return value.replace(/[\r\n|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80).trim().toLowerCase();
}

export type ContextsValidation = { ok: true; contexts: string[] } | { ok: false; error: string };

/**
 * A list of GTD contexts as a profile stores it: one to thirty, each trimmed
 * and non-blank, at most 40 characters, and no two the same by contextKey
 * (the database refuses anything else).
 */
export function validateGtdContexts(values: readonly string[]): ContextsValidation {
  const contexts = values.map(value => value.trim());
  if (contexts.length === 0) return { ok: false, error: 'Keep at least one context' };
  if (contexts.length > CONTEXTS_MAX) return { ok: false, error: `Keep at most ${CONTEXTS_MAX} contexts` };
  if (contexts.some(context => !context)) return { ok: false, error: 'A context needs a name' };
  // Characters, as the database's char_length counts them (not UTF-16 units).
  const long = contexts.find(context => [...context].length > CONTEXT_MAX_LENGTH);
  if (long) return { ok: false, error: `"${long}" is longer than ${CONTEXT_MAX_LENGTH} characters` };
  const seen = new Map<string, string>();
  for (const context of contexts) {
    const same = seen.get(contextKey(context));
    if (same !== undefined) return { ok: false, error: `"${context}" is the same as "${same}"` };
    seen.set(contextKey(context), context);
  }
  return { ok: true, contexts };
}

/**
 * The user's own spelling of a context, matched as the database matches it
 * (by contextKey), or null when none of their contexts matches.
 */
export function ownContext(context: string, contexts: readonly string[]): string | null {
  const key = contextKey(context);
  return contexts.find(own => contextKey(own) === key) ?? null;
}

/** A task lives in a Project or an Area; a note may live in any container. */
export function allowedContainerKinds(kind: ItemKind): readonly ContainerKind[] {
  return kind === 'task' ? ['project', 'area'] : ['project', 'area', 'resource'];
}

export function isItemKind(value: unknown): value is ItemKind {
  return value === 'task' || value === 'note';
}

export function isContainerKind(value: unknown): value is ContainerKind {
  return value === 'project' || value === 'area' || value === 'resource';
}

export function isGtdStatus(value: unknown): value is GtdStatus {
  return value === 'next' || value === 'waiting' || value === 'someday';
}

/** A task's container columns after moving it into a project or an area: that one set, the other cleared. */
export function taskMoveColumns(target: ContainerRef): { project_id: string | null; area_id: string | null } {
  if (target.kind === 'resource') throw new Error('A task lives in a Project or an Area, not a Resource');
  return { project_id: target.kind === 'project' ? target.id : null, area_id: target.kind === 'area' ? target.id : null };
}

/** A note's container columns after moving it into any container: that one set, the others cleared. */
export function noteMoveColumns(target: ContainerRef): { project_id: string | null; area_id: string | null; resource_id: string | null } {
  return {
    project_id: target.kind === 'project' ? target.id : null,
    area_id: target.kind === 'area' ? target.id : null,
    resource_id: target.kind === 'resource' ? target.id : null,
  };
}

type Target<K extends ContainerKind> = { kind: K; id: string } | { kind: K; newName: string };

/** Where to file: an existing container, or a new one created with the filing. */
export type FilingTarget = Target<ContainerKind>;

/** What file_capture_into() files. */
export type FilingRequest =
  | {
      kind: 'task';
      title: string;
      due_date: string | null;
      /** When to remind the user: a wall time in their zone (YYYY-MM-DDTHH:MM), or none. */
      reminder: string | null;
      notes: string | null;
      gtd_status: GtdStatus;
      /** Only a next action has a context. */
      context: string | null;
      container: Target<'project' | 'area'>;
      personIds: string[];
      newPersonNames: string[];
    }
  | {
      kind: 'note';
      title: string;
      body: string | null;
      container: Target<ContainerKind>;
      personIds: string[];
      newPersonNames: string[];
    };

/**
 * Filing form values before validation. `details` is a task's notes or a
 * note's body, so switching between Task and Note keeps user edits; the GTD
 * fields are kept too and used only for a task.
 */
export interface FilingRequestDraft {
  kind: ItemKind;
  title: string;
  due_date: string | null;
  /** A task's reminder, a wall time in the user's zone; kept only for a task. */
  reminder: string | null;
  /** A suggested reminder whose time had passed when the draft was made: shown as passed, not set. */
  expiredReminder: string | null;
  details: string | null;
  gtdStatus: GtdStatus;
  context: string | null;
  container: FilingTarget | null;
  personIds: string[];
  /** New people to create with the filing: only names the user chose. */
  newPersonNames: string[];
  /** Names the AI heard that match no one in the user's people; each is offered as a one-tap "+ Add". */
  proposedPeople: string[];
}

/**
 * When a draft is made: the user's zone (null until it is set) and now. A
 * suggested reminder is judged in that zone; with no zone it is kept, and
 * filing then says the reminder needs a zone.
 */
export interface FilingClock {
  timeZone: string | null;
  now: Date;
}

/** A classification as the filing helpers read it. */
export interface FilingSuggestion {
  category: string;
  extracted_fields: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map(text).filter((s): s is string => s !== null) : [];
}

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** The kind a suggestion files as. Legacy person, project and idea suggestions become notes. */
function suggestedKind(category: string): ItemKind {
  return isItemKind(category) ? category : 'note';
}

/**
 * The suggestion's container if it is active and allowed for the kind: an
 * offered container, or a proposed new one (an active container of that kind
 * and name is used instead, as the server would).
 */
function suggestedTarget(kind: ItemKind, fields: Record<string, unknown>, containers: readonly ContainerRow[]): FilingTarget | null {
  const allowed = allowedContainerKinds(kind);
  const active = containers.filter(c => !c.archived_at);
  const ref = fields.container;
  if (isRecord(ref) && isContainerKind(ref.kind) && allowed.includes(ref.kind)) {
    const found = active.find(c => c.kind === ref.kind && c.id === ref.id);
    if (found) return { kind: found.kind, id: found.id };
  }
  const proposed = fields.proposed_container;
  if (isRecord(proposed) && isContainerKind(proposed.kind) && allowed.includes(proposed.kind)) {
    const name = text(proposed.name);
    if (name) {
      const existing = active.find(c => c.kind === proposed.kind && sameName(c.name, name));
      return existing ? { kind: existing.kind, id: existing.id } : { kind: proposed.kind, newName: name };
    }
  }
  return null;
}

/**
 * Starting values for filing a capture. Reads new suggestions (title, due_date,
 * reminder, gtd_status, context, container, proposed_container, person_ids,
 * proposed_people) and legacy names. Details come from the capture. A due
 * date or a reminder appears only when a task suggestion carried a real one (a
 * reminder whose time has passed by `clock` is kept as expiredReminder instead), and a
 * container only when it is active and allowed for the kind. Nothing is
 * invented, and no person is created unless chosen: proposed names start as
 * proposals. With no suggestion the draft is a next-action task with no
 * container.
 */
export function defaultFilingRequest(
  rawText: string,
  suggestion: FilingSuggestion | null | undefined,
  containers: readonly ContainerRow[],
  clock: FilingClock,
): FilingRequestDraft {
  const fields = suggestion && isRecord(suggestion.extracted_fields) ? suggestion.extracted_fields : {};
  const kind = suggestion ? suggestedKind(suggestion.category) : 'task';
  const title = text(fields.title) ?? text(fields.name) ?? captureTitle(rawText);
  const legacy = text(fields.legacy_category) !== null || (suggestion !== null && suggestion !== undefined && !isItemKind(suggestion.category));
  const gtdStatus = kind === 'task' && isGtdStatus(fields.gtd_status) ? fields.gtd_status : 'next';
  const suggestedReminder = kind === 'task' && isWallTime(fields.reminder) ? fields.reminder : null;
  // Filing would refuse a reminder whose time has passed: it is shown as passed instead.
  const reminderPassed =
    suggestedReminder !== null && clock.timeZone !== null && suggestedReminder <= wallTimeIn(clock.timeZone, clock.now);
  return {
    kind,
    title,
    due_date: kind === 'task' && isCalendarDate(fields.due_date) ? fields.due_date : null,
    reminder: reminderPassed ? null : suggestedReminder,
    expiredReminder: reminderPassed ? suggestedReminder : null,
    details: itemTextFromCapture(kind, rawText, title),
    gtdStatus,
    context: kind === 'task' && gtdStatus === 'next' && !legacy ? text(fields.context) : null,
    container: suggestedTarget(kind, fields, containers),
    personIds: strings(fields.person_ids),
    newPersonNames: [],
    proposedPeople: strings(fields.proposed_people),
  };
}

export type FilingValidation = { ok: true; request: FilingRequest } | { ok: false; error: string };

/** Check a draft and turn it into the request file_capture_into() takes. */
export function validateFilingRequest(draft: FilingRequestDraft): FilingValidation {
  const label = KIND_LABELS[draft.kind];
  const title = draft.title.trim();
  if (!title) return { ok: false, error: `A ${label.toLowerCase()} needs a title` };
  const container = draft.container;
  if (!container) return { ok: false, error: `Choose where this ${label.toLowerCase()} lives` };
  if (!allowedContainerKinds(draft.kind).includes(container.kind)) {
    return { ok: false, error: 'A task lives in a Project or an Area, not a Resource' };
  }
  const target: FilingTarget = 'newName' in container ? { kind: container.kind, newName: container.newName.trim() } : container;
  if ('newName' in target && !target.newName) {
    return { ok: false, error: `A new ${CONTAINER_KIND_LABELS[target.kind]} needs a name` };
  }
  const newPersonNames = draft.newPersonNames.map(name => name.trim());
  if (newPersonNames.some(name => !name)) return { ok: false, error: 'A new person needs a name' };
  const details = draft.details?.trim() || null;

  if (draft.kind === 'task') {
    if (draft.due_date !== null && !isCalendarDate(draft.due_date)) {
      return { ok: false, error: 'A due date must be a real date (YYYY-MM-DD)' };
    }
    if (draft.reminder !== null && !isWallTime(draft.reminder)) {
      return { ok: false, error: 'A reminder must be a real date and time' };
    }
    if (!isGtdStatus(draft.gtdStatus)) return { ok: false, error: 'Choose next action, waiting for, or someday' };
    return {
      ok: true,
      request: {
        kind: 'task',
        title,
        due_date: draft.due_date,
        reminder: draft.reminder,
        notes: details,
        gtd_status: draft.gtdStatus,
        context: draft.gtdStatus === 'next' ? draft.context?.trim() || null : null,
        container: target as Target<'project' | 'area'>,
        personIds: draft.personIds,
        newPersonNames,
      },
    };
  }
  return {
    ok: true,
    request: { kind: 'note', title, body: details, container: target, personIds: draft.personIds, newPersonNames },
  };
}

/**
 * The arguments of the delete_item() database function: delete the signed-in
 * user's task or note and, when `deleteCapture`, the capture it was filed from.
 */
export function deleteItemRpcArgs(kind: ItemKind, id: string, deleteCapture: boolean): Record<string, unknown> {
  return { p_kind: kind, p_item_id: id, p_delete_capture: deleteCapture };
}

/** A capture a task or note was filed from. */
export interface SourceCapture {
  id: string;
  rawText: string;
}

/**
 * The captures a task or note was filed from, one each, from the rows of
 * `classifications.select('capture_id, captures(raw_text)')`. Throws on a row
 * it cannot read, so a capture is never left out unseen.
 */
export function sourceCapturesFrom(rows: unknown): SourceCapture[] {
  if (!Array.isArray(rows)) throw new Error('the capture this came from could not be read');
  const found = new Map<string, SourceCapture>();
  for (const row of rows as unknown[]) {
    const fields = typeof row === 'object' && row !== null ? (row as Record<string, unknown>) : null;
    const capture = fields && typeof fields.captures === 'object' && fields.captures !== null
      ? (fields.captures as Record<string, unknown>)
      : null;
    if (!fields || typeof fields.capture_id !== 'string' || !capture || typeof capture.raw_text !== 'string') {
      throw new Error('the capture this came from could not be read');
    }
    found.set(fields.capture_id, { id: fields.capture_id, rawText: capture.raw_text });
  }
  return [...found.values()];
}

/** How many captures delete_item() deleted. Throws when its answer is not a count. */
export function capturesDeleted(data: unknown): number {
  if (typeof data !== 'number' || !Number.isInteger(data) || data < 0) {
    throw new Error('deleting did not say how many captures went with it');
  }
  return data;
}

/** The arguments of the file_capture_into() database function for a request. */
export function filingRpcArgs(captureId: string, request: FilingRequest): Record<string, unknown> {
  const fields = request.kind === 'task'
    ? {
        title: request.title,
        due_date: request.due_date,
        reminder: request.reminder,
        notes: request.notes,
        gtd_status: request.gtd_status,
        context: request.context,
      }
    : { title: request.title, body: request.body };
  return {
    p_capture_id: captureId,
    p_kind: request.kind,
    p_container_kind: request.container.kind,
    p_container_id: 'id' in request.container ? request.container.id : null,
    p_new_container_name: 'newName' in request.container ? request.container.newName : null,
    p_fields: fields,
    p_person_ids: request.personIds,
    p_new_person_names: request.newPersonNames,
  };
}

/**
 * The request that files captured words with the AI's suggested metadata
 * (the Inbox's Confirm), or null when the suggestion cannot be filed as it stands: a
 * legacy category, no title, no usable container or proposal, or a suggested
 * reminder whose time has passed (the filing form shows that). A proposed
 * container is created with the filing; proposed people are not (the caller
 * adds the ones the user taps). A task's context is sent in the user's own
 * spelling from `contexts`, or not at all when it is no longer in that list.
 */
export function confirmableSuggestion(
  rawText: string,
  suggestion: FilingSuggestion | null | undefined,
  containers: readonly ContainerRow[],
  contexts: readonly string[],
  clock: FilingClock,
): FilingRequest | null {
  if (!suggestion || !isItemKind(suggestion.category) || !isRecord(suggestion.extracted_fields)) return null;
  if (!text(suggestion.extracted_fields.title)) return null;
  const draft = defaultFilingRequest(rawText, suggestion, containers, clock);
  if (draft.expiredReminder !== null) return null;
  const result = validateFilingRequest(draft);
  if (!result.ok) return null;
  const request = result.request;
  // A context no longer in the user's list (renamed or removed since) is dropped, never sent.
  return request.kind === 'task' && request.context !== null
    ? { ...request, context: ownContext(request.context, contexts) }
    : request;
}

/**
 * Why a suggestion waits in the Inbox when the reminder its capture asked for
 * had passed by the time it was filed (review reason reminder_passed, CER-81):
 * "The reminder time, yesterday at 3:55 PM, had passed when this was filed".
 * Null for any other suggestion. Filing late is normal (a retry, the server's
 * sweep of captures nothing filed), so the Inbox says which time it was.
 */
export function passedReminderNote(
  suggestion: FilingSuggestion | null | undefined,
  timeZone: string | null,
  now: Date,
): string | null {
  const fields = suggestion && isRecord(suggestion.extracted_fields) ? suggestion.extracted_fields : null;
  if (!fields || !strings(fields.review_reasons).includes('reminder_passed')) return null;
  return isWallTime(fields.reminder)
    ? `The reminder time, ${wallTimePhrase(fields.reminder, timeZone, now)}, had passed when this was filed`
    : 'The reminder time had passed when this was filed';
}

/**
 * "Task in Home", "Note in new Resource 'Recipes'", or just "Task" when the
 * suggestion names no usable container. Null for a legacy category suggestion.
 */
export function describeSuggestion(suggestion: FilingSuggestion, containers: readonly ContainerRow[]): string | null {
  if (!isItemKind(suggestion.category)) return null;
  const kind = suggestion.category;
  const fields = isRecord(suggestion.extracted_fields) ? suggestion.extracted_fields : {};
  const target = suggestedTarget(kind, fields, containers);
  if (!target) return KIND_LABELS[kind];
  if ('newName' in target) return `${KIND_LABELS[kind]} in new ${CONTAINER_KIND_LABELS[target.kind]} '${target.newName}'`;
  const container = containers.find(c => c.kind === target.kind && c.id === target.id) as ContainerRow;
  return `${KIND_LABELS[kind]} in ${container.name}`;
}

/** A container as the classify-capture response names it. */
export interface NamedContainer {
  kind: ContainerKind;
  id: string;
  name: string | null;
}

/** What AI filing did with a capture, as the classify-capture response reports it. */
export type AutoFilingOutcome =
  | { status: 'auto_filed'; category: ItemKind; destinationId: string; container: NamedContainer }
  | {
      status: 'needs_review';
      category: ItemKind;
      container: NamedContainer | null;
      proposedContainer: { kind: ContainerKind; name: string } | null;
    };

function namedContainer(value: unknown): NamedContainer | null {
  if (!isRecord(value) || !isContainerKind(value.kind) || typeof value.id !== 'string' || !value.id) return null;
  return { kind: value.kind, id: value.id, name: text(value.name) };
}

/**
 * Read the `classification` in a successful classify-capture response. Null
 * when it does not say the capture was filed (and where) or sent to review, so
 * nothing is reported as filed unless the server said so.
 */
export function autoFilingOutcome(classification: unknown): AutoFilingOutcome | null {
  if (!isRecord(classification)) return null;
  const { status, category, destination_id: destinationId } = classification;
  if (!isItemKind(category)) return null;
  const container = namedContainer(classification.container);
  if (status === 'auto_filed') {
    return typeof destinationId === 'string' && destinationId && container
      ? { status, category, destinationId, container }
      : null;
  }
  if (status !== 'needs_review') return null;
  const proposed = classification.proposed_container;
  const proposedName = isRecord(proposed) ? text(proposed.name) : null;
  return {
    status,
    category,
    container,
    proposedContainer: isRecord(proposed) && isContainerKind(proposed.kind) && proposedName ? { kind: proposed.kind, name: proposedName } : null,
  };
}

/** "Task in Home", "Task in new Project 'Kitchen remodel'", or "Task" for an outcome. */
export function describeOutcome(outcome: AutoFilingOutcome): string {
  const kind = KIND_LABELS[outcome.category];
  if (outcome.container?.name) return `${kind} in ${outcome.container.name}`;
  if (outcome.status === 'needs_review' && outcome.proposedContainer) {
    return `${kind} in new ${CONTAINER_KIND_LABELS[outcome.proposedContainer.kind]} '${outcome.proposedContainer.name}'`;
  }
  return kind;
}
